import stackStyles from '../components/sections/stack-section/StackList.module.css'
import {EffectiveEntryStatus} from './effective-entry-status'
import type {StackSectionStackEntry} from './merge-box-status-calculator/stack-section-status'

// Each effective entry status maps to a "tone" — a Primer color bucket that
// matches the status icon's color (see the `effective-entry-status-icons`).
// The vertical timeline line segment between two icons is drawn end-to-end in
// the upper entry's tone so the line flowing down from an icon, and the icon
// and label it connects to, all read as the same status (per `line-colors.png`).
type LineTone = 'default' | 'muted' | 'attention' | 'danger' | 'success' | 'done'

const EFFECTIVE_STATUS_TONE: Record<EffectiveEntryStatus, LineTone> = {
  [EffectiveEntryStatus.After]: 'muted',
  [EffectiveEntryStatus.BlockedDownstack]: 'attention',
  [EffectiveEntryStatus.Closed]: 'danger',
  [EffectiveEntryStatus.Draft]: 'muted',
  [EffectiveEntryStatus.Loading]: 'default',
  [EffectiveEntryStatus.MergeConflicts]: 'attention',
  [EffectiveEntryStatus.Merged]: 'done',
  [EffectiveEntryStatus.Merging]: 'done',
  [EffectiveEntryStatus.NeedsRebase]: 'attention',
  [EffectiveEntryStatus.NotReady]: 'attention',
  [EffectiveEntryStatus.Queued]: 'attention',
  [EffectiveEntryStatus.Ready]: 'success',
}

// Tone classes set the corresponding CSS custom property on the `<li>` so the
// `::before` and inner `<a>::after` line segments can pick it up. Two parallel
// class families (above/below) let each segment between two icons match the
// upper entry's color end-to-end (rather than splitting at the icon).
const LINE_ABOVE_CLASS: Record<LineTone, string> = {
  default: stackStyles.lineAboveDefault,
  muted: stackStyles.lineAboveMuted,
  attention: stackStyles.lineAboveAttention,
  danger: stackStyles.lineAboveDanger,
  success: stackStyles.lineAboveSuccess,
  done: stackStyles.lineAboveDone,
}

const LINE_BELOW_CLASS: Record<LineTone, string> = {
  default: stackStyles.lineBelowDefault,
  muted: stackStyles.lineBelowMuted,
  attention: stackStyles.lineBelowAttention,
  danger: stackStyles.lineBelowDanger,
  success: stackStyles.lineBelowSuccess,
  done: stackStyles.lineBelowDone,
}

function entryTone(entry: StackSectionStackEntry): LineTone {
  const status = entry.effectiveEntryStatus ?? EffectiveEntryStatus.NotReady
  return EFFECTIVE_STATUS_TONE[status]
}

// Returns the className(s) to apply to a stack entry's `NavList.Item` so its
// vertical line segments above and below the icon match the design, based on
// each entry's `effectiveEntryStatus`.
//
// The line above this entry's icon (the `::before` stub) belongs to — and
// matches the color of — the previous (upper) entry, so the segment between two
// icons reads as one continuous color (the upper entry's). The first entry has
// no line above (hidden via `:first-child::before` in CSS) so the fallback to
// the entry's own tone there is harmless.
export function getEffectiveEntryLineClass(
  entry: StackSectionStackEntry,
  previousEntry?: StackSectionStackEntry,
): string {
  const below = entryTone(entry)
  const above = previousEntry ? entryTone(previousEntry) : below
  return `${LINE_ABOVE_CLASS[above]} ${LINE_BELOW_CLASS[below]}`
}

// Returns the className(s) to apply to the base reference name item (`<li>`) in
// the stack, based on each entry's `effectiveEntryStatus`. Uses the last entry (closest to trunk)
// in the stack to determine the line color above the base ref.
export function getEffectiveBaseRefLineClass(stackEntries: StackSectionStackEntry[]): string {
  if (stackEntries.length === 0) return ''

  const firstEntry = stackEntries[stackEntries.length - 1]

  if (!firstEntry) return ''

  return LINE_ABOVE_CLASS[entryTone(firstEntry)]
}
